import React, { useState, useRef, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Pressable,
  Linking,
} from "react-native";
import {
  AntDesign,
  Feather,
  Ionicons,
  MaterialIcons,
} from "@expo/vector-icons";
import * as Clipboard from "@react-native-clipboard/clipboard";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  FloatingPortal,
  FloatingFocusManager,
  useDismiss,
  useRole,
  useInteractions,
} from "@floating-ui/react";

import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import Fonts from "../../../Resources/Fonts";
import { useDevice } from "../../_layout";
import Tooltip from "../Tooltip";
import { getFloatingExerciseLinkTriggerChildren } from "./floatingExerciseLinkTriggerChildren";

const FLOATING_EXERCISE_LINK_Z_INDEX = 130000;

const FloatingExerciseLink = observer(
  ({
    exercise,
    onSave,
    children,
    triggerStyle,
    onClose,
    isOpen: externalIsOpen,
    onOpenChange,
    portalRoot = null,
  }) => {
    const { t } = useTranslation();
    const { isMobile, isIOS } = useDevice();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [linkText, setLinkText] = useState(exercise?.link || "");
    const [isDeleteHovered, setIsDeleteHovered] = useState(false);
    const [isInputHovered, setIsInputHovered] = useState(false);
    const [isButtonHovered, setIsButtonHovered] = useState(false);
    const [isCloseHovered, setIsCloseHovered] = useState(false);
    const [isPasteHovered, setIsPasteHovered] = useState(false);
    const [isPositioned, setIsPositioned] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const anchorRef = useRef(null);
    const textInputRef = useRef(null);
    const isHandlingPasteRef = useRef(false);

    // Use external state if provided, otherwise use internal state
    const isOpen =
      externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
    const setIsOpen = onOpenChange || setInternalIsOpen;

    const { refs, floatingStyles, context } = useFloating({
      open: isOpen,
      onOpenChange: (open) => {
        setIsOpen(open);
        if (!open && onClose) {
          onClose();
        }
      },
      placement: "right-start",
      middleware: [
        offset(8),
        flip({
          fallbackPlacements: ["left-start", "top", "bottom", "right", "left"],
        }),
        size({
          apply({ availableWidth, availableHeight, elements }) {
            // Apply size constraints directly to the floating element during positioning
            Object.assign(elements.floating.style, {
              maxWidth: `${Math.max(
                280,
                Math.min(320, availableWidth - 32),
              )}px`,
              maxHeight: `${Math.max(200, availableHeight - 32)}px`,
            });
          },
          padding: 16,
        }),
        shift({
          padding: 16,
        }),
      ],
      whileElementsMounted: autoUpdate,
    });

    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "dialog" });
    const { getFloatingProps } = useInteractions([dismiss, role]);

    // Anchor to external ref
    useEffect(() => {
      if (anchorRef?.current) {
        refs.setReference(anchorRef.current);
        // Set positioned to true once the reference is set
        setIsPositioned(true);
      }
    }, [anchorRef, refs]);

    // Handle positioning when opening/closing
    useEffect(() => {
      if (!isOpen) {
        setIsPositioned(false);
      } else if (isOpen && anchorRef?.current) {
        // When opened externally (via isOpen prop), ensure reference is set
        refs.setReference(anchorRef.current);
        setIsPositioned(true);
      }
    }, [isOpen, refs]);

    // Sync linkText with exercise.link
    useEffect(() => {
      setLinkText(exercise?.link || "");
      setErrorMessage(""); // Clear error when link changes
    }, [exercise?.link, isOpen]);

    useEffect(() => {
      if (!isOpen || !isPositioned || isMobile) return;
      const id = requestAnimationFrame(() => {
        textInputRef.current?.focus?.();
      });
      return () => cancelAnimationFrame(id);
    }, [isOpen, isPositioned, isMobile]);

    const handleSaveLink = async (newValue) => {
      const trimmedValue = newValue.trim();

      // If empty, just save it (allow clearing the link)
      if (!trimmedValue) {
        if (exercise && exercise.setProperty) {
          exercise.setProperty("link", "");
        }
        setErrorMessage("");
        return;
      }

      try {
        // First check: Web-specific validation to reject obviously invalid inputs like "d"
        const isValidWebURL = (url) => {
          // Must have at least a dot (domain structure) or protocol
          if (!url.includes(".") && !url.includes("://")) {
            return false;
          }

          // Try to create a URL object for validation
          try {
            // If it doesn't start with protocol, try adding https://
            const testUrl =
              url.startsWith("http://") || url.startsWith("https://")
                ? url
                : `https://${url}`;

            const urlObj = new URL(testUrl);

            // Must have a valid hostname with at least one dot (for TLD)
            if (!urlObj.hostname || !urlObj.hostname.includes(".")) {
              return false;
            }

            // Hostname should be more than just a single character
            if (urlObj.hostname.length < 3) {
              return false;
            }

            return true;
          } catch {
            return false;
          }
        };

        // Second check: Mobile app schemes and special URLs
        const isMobileCompatibleURL = (url) => {
          // Common mobile app schemes that should be allowed
          const mobileSchemes = [
            "whatsapp:",
            "telegram:",
            "instagram:",
            "twitter:",
            "facebook:",
            "youtube:",
          ];

          return mobileSchemes.some((scheme) =>
            url.toLowerCase().startsWith(scheme),
          );
        };

        // Check web URL format first (rejects single chars like "d")
        const passesWebValidation = isValidWebURL(trimmedValue);

        // Check if it's a mobile app scheme
        const isMobileURL = isMobileCompatibleURL(trimmedValue);

        // If it doesn't pass basic web validation and isn't a mobile scheme, reject it
        if (!passesWebValidation && !isMobileURL) {
          setErrorMessage("invalidUrl-string");
          return;
        }

        // Final check with Linking.canOpenURL (this will be permissive on web but strict on mobile)
        const canOpen = await Linking.canOpenURL(trimmedValue);
        console.log("Can open URL:", canOpen, trimmedValue);

        // If Linking says it can't open it, reject it
        if (!canOpen) {
          setErrorMessage("invalidUrl-string");
          return;
        }

        // Clear error and save the link as-is
        setErrorMessage("");
        if (exercise && exercise.setProperty) {
          exercise.setProperty("link", trimmedValue);
        }
        if (onSave) {
          onSave(trimmedValue);
        }
      } catch (error) {
        setErrorMessage("Error: Invalid URL format");
        console.error("URL validation error:", error);
      }
    };

    const handleCopyLink = async () => {
      const trimmedLink = linkText.trim();
      if (trimmedLink) {
        try {
          // For web, use the native browser clipboard API
          if (typeof navigator !== "undefined" && navigator.clipboard) {
            await navigator.clipboard.writeText(trimmedLink);
          } else {
            // Fallback for older browsers or React Native
            Clipboard.setString(trimmedLink);
          }
          // await Linking.openURL(trimmedLink);
        } catch (error) {
          console.error("Failed to copy link:", error);
        }
      }
    };

    const handleDeleteLink = () => {
      setLinkText("");
      setErrorMessage(""); // Clear error when deleting
      if (exercise) {
        exercise.setProperty("link", "");
      }
    };

    // On web, use navigator.clipboard.readText()
    // Safari shows a paste prompt that user must tap to allow access
    const handlePasteLink = async () => {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        try {
          isHandlingPasteRef.current = true;
          const text = await navigator.clipboard.readText();
          if (text && text.trim()) {
            const trimmedText = text.trim();
            setLinkText(trimmedText);
            setErrorMessage("");
            // Automatically save after pasting
            await handleSaveLink(trimmedText);
          }
        } catch {
          // Silently fail - user can paste manually
        } finally {
          // Reset flag after a short delay to allow onChangeText to process
          setTimeout(() => {
            isHandlingPasteRef.current = false;
          }, 100);
        }
      }
    };

    const renderFloatingContent = () => {
      const isEmpty = !linkText || linkText.trim() === "";
      const hasExistingLink = exercise?.link && exercise.link.trim() !== "";
      const isUnchanged = linkText.trim() === exercise?.link;

      return (
        <View style={styles.contentContainer}>
          <View style={styles.header}>
            <Text style={styles.title}>
              {t("videoLink-string").toUpperCase()}
            </Text>
            <TouchableOpacity
              onPress={() => {
                setIsOpen(false);
                if (onClose) {
                  onClose();
                }
              }}
              style={styles.closeButton}
              onMouseEnter={() => !isMobile && setIsCloseHovered(true)}
              onMouseLeave={() => setIsCloseHovered(false)}
            >
              <AntDesign
                name="close"
                size={18}
                color={Colors.grayColor}
                style={{ opacity: isCloseHovered ? 0.6 : 1 }}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputContainer}>
            <View
              style={[
                styles.inputWrapper,
                isMobile && styles.inputWrapperMobile,
                isInputHovered && styles.inputWrapperHovered,
              ]}
              onMouseEnter={() => !isMobile && setIsInputHovered(true)}
              onMouseLeave={() => setIsInputHovered(false)}
            >
              <TextInput
                ref={textInputRef}
                style={[
                  styles.textInput,
                  isMobile && isIOS && styles.textInputIOS,
                ]}
                value={linkText}
                onChangeText={async (text) => {
                  const previousText = linkText;
                  setLinkText(text);
                  // Clear error when user starts typing
                  if (errorMessage) {
                    setErrorMessage("");
                  }

                  // Detect paste: check if text looks like a URL was pasted
                  // Also check if we're not already handling a programmatic paste
                  const trimmedText = text.trim();
                  const trimmedPrevious = previousText.trim();

                  // Check if it looks like a URL was pasted:
                  // 1. Contains URL patterns (http://, https://, www., or common TLDs)
                  // 2. Significant difference from previous text (either length or content)
                  // 3. Not already handling a programmatic paste
                  const hasUrlPattern =
                    /(https?:\/\/|www\.|\.(com|org|net|io|co|edu|gov))/i.test(
                      trimmedText,
                    );
                  const isSignificantChange =
                    trimmedText.length !== trimmedPrevious.length && // Length changed
                    (trimmedText.length > trimmedPrevious.length + 5 || // Added 5+ chars
                      Math.abs(trimmedText.length - trimmedPrevious.length) >
                        trimmedPrevious.length * 0.5); // Or changed by 50%+

                  const isLikelyPaste =
                    !isHandlingPasteRef.current &&
                    hasUrlPattern &&
                    isSignificantChange &&
                    trimmedPrevious.length > 0; // Not initial input

                  if (isLikelyPaste && trimmedText) {
                    // Small delay to ensure the paste is complete
                    setTimeout(async () => {
                      await handleSaveLink(trimmedText);
                    }, 100);
                  }
                }}
                onSubmitEditing={async () => {
                  // Save when Enter is pressed
                  if (linkText.trim()) {
                    await handleSaveLink(linkText);
                  }
                }}
                placeholder="URL"
                placeholderTextColor={Colors.grayColor}
                multiline={false}
                autoCapitalize="none"
                keyboardType="url"
                autoCorrect={false}
                maxLength={400}
                autoFocus={!isMobile}
                selectionColor={Colors.bubbleColor}
                returnKeyType="done"
              />
              {isEmpty && !(isIOS && isMobile) ? (
                <Tooltip content={t("paste-string")} placement="top">
                  <TouchableOpacity
                    style={[
                      styles.pasteButton,
                      isMobile && styles.pasteButtonMobile,
                    ]}
                    onPress={(e) => {
                      e?.stopPropagation?.();
                      handlePasteLink();
                    }}
                    onMouseEnter={() => !isMobile && setIsPasteHovered(true)}
                    onMouseLeave={() => setIsPasteHovered(false)}
                  >
                    <Ionicons
                      name="clipboard-outline"
                      size={18}
                      color={Colors.bubbleColor}
                      style={{
                        opacity: isPasteHovered ? 0.7 : 1,
                      }}
                    />
                  </TouchableOpacity>
                </Tooltip>
              ) : (
                <TouchableOpacity
                  style={[
                    styles.actionButton,
                    isMobile && styles.actionButtonMobile,
                  ]}
                  onPress={async () =>
                    isUnchanged
                      ? handleCopyLink()
                      : await handleSaveLink(linkText)
                  }
                  onMouseEnter={() => !isMobile && setIsButtonHovered(true)}
                  onMouseLeave={() => setIsButtonHovered(false)}
                >
                  {isUnchanged ? (
                    <Ionicons
                      name="copy-outline"
                      size={18}
                      color={Colors.bubbleColor}
                      style={{
                        opacity: isButtonHovered ? 0.7 : 1,
                      }}
                    />
                  ) : (
                    <Text
                      style={[
                        styles.saveButtonText,
                        isMobile && styles.saveButtonTextMobile,
                        isButtonHovered && styles.saveButtonTextHovered,
                      ]}
                    >
                      {t("save-string")}
                    </Text>
                  )}
                </TouchableOpacity>
              )}
            </View>
          </View>

          {errorMessage ? (
            <View
              style={[
                styles.errorContainer,
                isMobile && styles.errorContainerMobile,
              ]}
            >
              <Text style={styles.errorText}>{t(`${errorMessage}`)}</Text>
            </View>
          ) : null}

          {hasExistingLink && (
            <TouchableOpacity
              style={[
                styles.deleteButton,
                isMobile && styles.deleteButtonMobile,
                isDeleteHovered && styles.deleteButtonHovered,
              ]}
              onPress={handleDeleteLink}
              onMouseEnter={() => !isMobile && setIsDeleteHovered(true)}
              onMouseLeave={() => setIsDeleteHovered(false)}
            >
              <Ionicons
                name={isDeleteHovered ? "trash" : "trash-outline"}
                size={16}
                color={Colors.redColor}
              />
              <Text style={styles.deleteButtonText}>{t("delete-string")}</Text>
            </TouchableOpacity>
          )}
        </View>
      );
    };

    const handleTriggerPress = () => {
      if (isOpen) {
        // If already open, close it
        setIsOpen(false);
        if (onClose) {
          onClose();
        }
      } else {
        // If closed, open it
        // Ensure the reference is set before opening
        if (anchorRef?.current) {
          refs.setReference(anchorRef.current);
          setIsPositioned(true);
          setIsOpen(true);
        }
      }
    };

    const modalScopedPortalRoot =
      anchorRef.current?.closest?.('[data-efortcoach-floating-root="true"]') ??
      null;
    const resolvedPortalRoot =
      (portalRoot && portalRoot.isConnected === false ? null : portalRoot) ??
      modalScopedPortalRoot ??
      anchorRef.current?.ownerDocument?.body ??
      null;
    const triggerChildren = getFloatingExerciseLinkTriggerChildren(children);

    return (
      <View>
        <TouchableOpacity
          ref={anchorRef}
          onPress={handleTriggerPress}
          style={triggerStyle}
          collapsable={false}
        >
          {triggerChildren}
        </TouchableOpacity>

        {isOpen && isPositioned && (
          <FloatingPortal root={resolvedPortalRoot ?? undefined}>
            <FloatingFocusManager context={context} modal={false}>
              <View
                ref={refs.setFloating}
                style={[
                  {
                    position: "fixed",
                    zIndex: FLOATING_EXERCISE_LINK_Z_INDEX,
                    padding: isMobile ? 12 : 16,
                    borderRadius: 14,
                    backgroundColor: Colors.whiteColor,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                    borderWidth: 0.2,
                    borderColor: Colors.borderColor,
                    minWidth: 280,
                    maxWidth: "100%",
                    // Remove static maxWidth and maxHeight - let size middleware handle this
                    overflow: "hidden",
                  },
                  floatingStyles,
                ]}
                {...getFloatingProps({
                  onClick: (event) => event?.stopPropagation?.(),
                  onTouchStart: (event) => event?.stopPropagation?.(),
                })}
              >
                {renderFloatingContent()}
              </View>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </View>
    );
  },
);

const styles = StyleSheet.create({
  contentContainer: {
    width: "100%",
    maxWidth: "100%",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  title: {
    fontSize: 15,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  closeButton: {
    padding: 4,
  },
  inputContainer: {
    marginBottom: 0,
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.borderColor,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: Colors.whiteColor,
    minHeight: 44,
    width: "100%",
    maxWidth: "100%",
  },
  inputWrapperHovered: {
    borderColor: "rgba(0, 0, 0, 0.3)",
  },
  inputWrapperMobile: {
    paddingLeft: 10,
    paddingRight: 10,
  },
  textInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    color: Colors.blackColor,
    padding: 0,
    margin: 0,
    outlineStyle: "none",
    borderWidth: 0,
  },
  textInputIOS: {
    fontSize: 16, // Prevent zoom on iOS
    caretColor: Colors.bubbleColor, // Make cursor visible on iOS
    WebkitAppearance: "none", // Remove iOS default styling
  },
  pasteButton: {
    paddingLeft: 12,
    paddingVertical: 0,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  pasteButtonMobile: {
    paddingLeft: 10,
  },
  actionButton: {
    paddingLeft: 12,
    paddingVertical: 0,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  actionButtonMobile: {
    paddingLeft: 10,
  },
  saveButtonText: {
    fontSize: 14,
    color: Colors.bubbleColor,
    fontWeight: "500",
  },
  saveButtonTextMobile: {
    fontSize: 12,
  },
  saveButtonTextHovered: {
    opacity: 0.7,
  },
  errorContainer: {
    marginTop: 12,
    marginBottom: 0,
  },
  errorContainerMobile: {
    marginTop: 8,
  },
  errorText: {
    fontSize: 12,
    color: Colors.redColor,
    fontWeight: "500",
  },
  deleteButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: Colors.redColor,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginTop: 12,
    backgroundColor: "transparent",
  },
  deleteButtonMobile: {
    marginTop: 8,
  },
  deleteButtonHovered: {
    backgroundColor: "rgba(240, 68, 56, 0.05)",
  },
  deleteButtonText: {
    fontSize: 14,
    color: Colors.redColor,
    fontWeight: "500",
  },
});

export default FloatingExerciseLink;
